import API from '@src/API.js';

const Helpers = {
  uuidv4() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        var r = (Math.random() * 16) | 0,
          v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  },
  host() {
    return import.meta.env.host;
  },
  exception(ex) {
    alert(ex);
  },
  url(page) {
    return this.host() + page;
  },
  api(page) {
    return this.host() + "/api" + page;
  },
  isNumber(n) {
    return !isNaN(parseFloat(n)) && !isNaN(n - 0);
  },
  findIndexWithAttr(array, attr, value) {
    if (array && attr && value && Array.isArray(array)) {
      for (var i = 0; i < array.length; i += 1) {
        if (array[i][attr] === value) {
          return i;
        }
      }
    }
    return -1;
  },
  findWithAttr(array, attr, value) {
    if (array && attr && value && Array.isArray(array)) {
      for (var i = 0; i < array.length; i += 1) {
        if (array[i][attr] === value) {
          return array[i];
        }
      }
    }
    return undefined;
  },
  uniq(a) {
    var seen = {};
    var out = [];
    var len = a.length;
    var j = 0;
    for (var i = 0; i < len; i++) {
      var item = a[i];
      if (seen[item] !== 1) {
        seen[item] = 1;
        out[j++] = item;
      }
    }
    return out;
  },
  propertyExists(obj, prop) {
    if (!obj || !prop) return false;
    var index = prop.indexOf(".");
    if (index === -1) return obj.hasOwnProperty(prop);
    var sub = prop.substring(0, index);
    var end = prop.substring(index + 1);
    return this.propertyExists(obj[sub], end);
  },
  getObjectPropertyNames(obj) {
    if (typeof obj !== 'object' || obj === null) {
      throw new Error('Provided value is not an object');
    }
    return Object.keys(obj);
  },
  async StatesAsync(countryID) {
    const resp = await API.getAsync(`/Country/${countryID}/States`);
    if (resp && resp.ok) {
      return resp.jsonData;
    }
    return [];
  },
  async CitiesAsync(stateID) {
    const resp = await API.getAsync(`/State/Cities?StateID=${stateID}`);
    if (resp && resp.ok) {
      return resp.jsonData;
    }
    return [];
  },
  async EmployersAsync(searchTerm, signal) {
    const options = signal instanceof AbortSignal ? { signal } : {};
    const resp = await API.getAsync(
      "/EmployerSearch?Name=" + encodeURIComponent(searchTerm || ""),
      null,
      options
    );
    if (resp && resp.ok) return resp.jsonData;
    return [];
  },
  async SkillsAsync(searchTerm) {
    const resp = await API.getAsync("/Skill/Search?Term=" + encodeURIComponent(searchTerm));
    if (resp && resp.ok) return resp.jsonData;
    return [];
  },
  async AgreementAsync(ID) {
    const resp = await API.getAsync(`/Agreement/${ID}`);
    if (resp && resp.ok) return resp.jsonData;
    return null;
  },
  toggleValues(array, value, valuePropName) {
    var index = -1;
    if (!array) array = [];
    if (valuePropName)
      index = array.map((a) => a[valuePropName]).indexOf(value);
    else {
      if (array.length > 0) {
        if (typeof array[0] == "number") index = array.indexOf(value);
        else {
          console.error("Unable to determine the value to find in the array");
          debugger;
        }
      }
    }
    if (index >= 0) array.splice(index, 1);
    else array.push(value);
    return array;
  },

  /**
   * Login hard-gate: AgreementType.RequiredForLogin and not yet accepted.
   * Do not use RoleAgreement.Required for this.
   */
  pendingLoginAgreements(userData) {
    const data = userData || {};
    return (data.agreements || []).filter((a) => {
      const forLogin =
        a.RequiredForLogin === true || a.requiredForLogin === true;
      const accepted = !!(a.DateAccepted || a.dateAccepted);
      return forLogin && !accepted;
    });
  },

  /**
   * Role/hiring soft-gate: RoleAgreement.Required and not yet accepted.
   * Distinct from pendingLoginAgreements.
   */
  pendingRequiredAgreements(userData) {
    const data = userData || {};
    return (data.agreements || []).filter((a) => {
      const required = a.Required === true || a.required === true;
      const accepted = !!(a.DateAccepted || a.dateAccepted);
      return required && !accepted;
    });
  },

  /** Paths allowed while login agreement gate is active. */
  isLoginAgreementEscapePath(pathname) {
    const p = (pathname || "/").toLowerCase();
    const allowed = [
      "/agreementsignature",
      "/logout",
      "/terms",
      "/privacy",
      "/about",
      "/faq",
      "/contact",
      "/sms",
      "/sms-opt-in",
      "/sms-join",
    ];
    return allowed.some((x) => p === x || p.startsWith(x + "/"));
  },

  isSysAdmin(userData) {
    const person = (userData || {}).person || {};
    if (person.IsSysAdmin === true || person.isSysAdmin === true) return true;
    const roles = person.Roles || person.roles || [];
    return roles.some((r) => Number(r) === 1);
  },

  canHire(userData) {
    const employers = (userData || {}).employers || [];
    return employers.some((e) => {
      const roles = e.EmployerAdminRoles || e.employerAdminRoles || [];
      return Array.isArray(roles) && roles.length > 0;
    });
  },

  isGatedHiringPath(pathname) {
    const p = (pathname || "/").toLowerCase();
    const prefixes = [
      "/recruit",
      "/jobpostingedit",
      "/jobmatches",
      "/jobinterest",
      "/employerrecruitedit",
      "/employeradminedit",
      "/employerroleedit",
      "/packageedit",
      "/packageitemedit",
      "/addemployervip",
      "/admin",
      "/adminagreements",
      "/adminskilledit",
      "/adminemployeredit",
      "/adminpackageitemtypes",
    ];
    return prefixes.some((x) => p === x || p.startsWith(x + "/"));
  },

  isAgreementEscapePath(pathname) {
    const p = (pathname || "/").toLowerCase();
    const allowed = [
      "/",
      "/dashboard",
      "/account",
      "/logout",
      "/agreementsignature",
      "/personedit",
      "/about",
    ];
    return allowed.some((x) => p === x || p.startsWith(x + "/"));
  },

  /** PersonConnectionStatus: Potential=1, Verified=2, Rejected=3, Neglected=4, NotInterested=5, Deleted=255 */
  connectionStatusLabel(status) {
    if (status == null || status === "") return "";
    const labels = {
      1: "Potential",
      2: "Verified",
      3: "Rejected",
      4: "Neglected",
      5: "Not interested",
      255: "Deleted",
      Potential: "Potential",
      Verified: "Verified",
      Rejected: "Rejected",
      Neglected: "Neglected",
      NotInterested: "Not interested",
      Deleted: "Deleted",
    };
    const key = typeof status === "number" ? status : String(status).trim();
    if (labels[key]) return labels[key];
    const asNum = Number(key);
    if (!Number.isNaN(asNum) && labels[asNum]) return labels[asNum];
    return String(status);
  },

  isConnectionPotential(status) {
    return this.connectionStatusLabel(status) === "Potential";
  },

  isConnectionVerified(status) {
    return this.connectionStatusLabel(status) === "Verified";
  },
};

export default Helpers;
